import { useState, useCallback, useRef } from "react";
import { IUser } from "../../models/IUser";
import { searchUsers } from "../../api/users";
import { AdminRole, AdminRoleLabels } from "../../types/adminRoles";
import { BrandGoogleSolid, Envelope } from "@mynaui/icons-react";
import { Button, Checkbox, Dialog, EmptyState, SearchInput, Spinner, Tag } from "../../ui";
import "./styles.scss";

interface AddUserRoleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (userId: string, roles: AdminRole[]) => Promise<void>;
}

const AccountIcon = ({ user }: { user: IUser }) =>
  user.accountType === "email" ? (
    <Envelope className="add-user-role__account" size={16} />
  ) : (
    <BrandGoogleSolid className="add-user-role__account" size={16} />
  );

const AddUserRoleDialog = ({ isOpen, onClose, onAssign }: AddUserRoleDialogProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<AdminRole[]>([]);
  const [assigning, setAssigning] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearch = useCallback(async (value: string) => {
    if (!value.trim()) {
      setUsers([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await searchUsers(value, 1, 10);
      setUsers(data.items);
    } catch (error: any) {
      console.error("Error searching users:", error.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback(
    (value: string) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(value);
      }, 500);
    },
    [handleSearch]
  );

  // SearchInput hands over the text itself
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleSelectUser = (user: IUser) => {
    setSelectedUser(user);
    // Pre-select any existing admin roles
    setSelectedRoles(user.adminRoles || []);
    setSearchValue("");
    setUsers([]);
  };

  const toggleRole = (role: AdminRole) => {
    setSelectedRoles((prev) => {
      if (prev.includes(role)) {
        return prev.filter((r) => r !== role);
      } else {
        return [...prev, role];
      }
    });
  };

  const handleAssign = async () => {
    if (!selectedUser || selectedRoles.length === 0) return;

    setAssigning(true);
    try {
      await onAssign(selectedUser.id, selectedRoles);
      handleClose();
    } catch (error) {
      console.error("Error assigning roles:", error);
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = () => {
    setSearchValue("");
    setUsers([]);
    setSelectedUser(null);
    setSelectedRoles([]);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title="Add User with Admin Role"
      busy={assigning}
      footer={
        selectedUser ? (
          <>
            <Button variant="ghost" onClick={handleClose} disabled={assigning}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={selectedRoles.length === 0} loading={assigning}>
              {assigning ? "Assigning..." : "Assign Roles"}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="add-user-role">
        {!selectedUser ? (
          <>
            <p className="add-user-role__instruction">Search for a user by name or email</p>
            <div className="add-user-role__search">
              <SearchInput
                block
                placeholder="Type to search users..."
                value={searchValue}
                onChange={handleSearchChange}
                autoFocus
              />
              {loading && <Spinner size="sm" className="add-user-role__search-spinner" />}
            </div>

            {users.length > 0 && (
              <div className="add-user-role__results">
                {users.map((user) => (
                  <button
                    type="button"
                    key={user.id}
                    className="add-user-role__result"
                    onClick={() => handleSelectUser(user)}
                  >
                    <span className="add-user-role__name-row">
                      <AccountIcon user={user} />
                      <span className="add-user-role__name">
                        {user.firstName} {user.lastName}
                      </span>
                    </span>
                    <span className="add-user-role__email">{user.email}</span>
                    {user.adminRoles && user.adminRoles.length > 0 && (
                      <span className="add-user-role__roles">
                        {user.adminRoles.map((role) => (
                          <Tag key={role} tone="brand">
                            {AdminRoleLabels[role]}
                          </Tag>
                        ))}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {searchValue && !loading && users.length === 0 && (
              <EmptyState compact title="No users found matching your search" />
            )}
          </>
        ) : (
          <>
            <div className="add-user-role__selected">
              <div className="add-user-role__selected-info">
                <p className="add-user-role__label">Selected User</p>
                <div className="add-user-role__name-row">
                  <AccountIcon user={selectedUser} />
                  <h4 className="add-user-role__selected-name">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </h4>
                </div>
                <p className="add-user-role__email">{selectedUser.email}</p>
              </div>
              <Button variant="tint" size="sm" onClick={() => setSelectedUser(null)}>
                Change User
              </Button>
            </div>

            <div>
              <p className="add-user-role__label">Select Admin Roles</p>
              <div className="add-user-role__options">
                {(Object.values(AdminRole).filter((v) => typeof v === "number") as AdminRole[]).map((role) => (
                  <Checkbox
                    key={role}
                    className={`add-user-role__option${selectedRoles.includes(role) ? " is-on" : ""}`}
                    checked={selectedRoles.includes(role)}
                    onChange={() => toggleRole(role)}
                    label={AdminRoleLabels[role]}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default AddUserRoleDialog;
